import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Spinner,
  Row,
  Col
} from 'reactstrap';
import { useParams, useNavigate } from 'react-router-dom';
import instance from '../../api/Axios';

const CreateRoomType = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  
  const [amenities, setAmenities] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    size_sqm: '',
    semester_price: '',
    trimester_price: '',
    annual_price: '',
    description: '',
    amenity_ids: []
  });

  useEffect(() => {
    fetchAmenities();
    fetchRoomTypes();
  }, []);

  const fetchAmenities = async () => {
    try {
      setLoading(true);
      
      // Fetch available amenities for this specific property
      // This includes global amenities + local amenities for this property's provider
      const response = await instance.get(`/api/properties/${propertyId}/available-amenities/`);
      
      // Determine type for display purposes
      const amenitiesWithType = response.data.map(amenity => ({
        ...amenity,
        type: amenity.amenity_type || (amenity.provider_content_type ? 'local' : 'global')
      }));
      
      setAmenities(amenitiesWithType);
    } catch (error) {
      console.error('Error fetching amenities:', error);
      setError('Failed to load amenities');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomTypes = async () => {
    try {
      const response = await instance.get(`/api/room-types/?property=${propertyId}`);
      setRoomTypes(response.data);
    } catch (error) {
      console.error('Error fetching room types:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Validate field on blur
    const errors = { ...validationErrors };
    
    switch (name) {
      case 'name':
        if (!value || !value.trim()) {
          errors.name = 'Room type name is required';
        } else {
          delete errors.name;
        }
        break;
      
      case 'size_sqm':
        if (!value) {
          errors.size_sqm = 'Room size is required';
        } else if (parseFloat(value) <= 0) {
          errors.size_sqm = 'Room size must be greater than 0';
        } else {
          delete errors.size_sqm;
        }
        break;
      
      case 'semester_price':
        if (!value || value.trim() === '') {
          errors.semester_price = 'Semester price is required';
        } else if (parseFloat(value) <= 0) {
          errors.semester_price = 'Semester price must be greater than 0';
        } else {
          delete errors.semester_price;
        }
        break;
      
      case 'trimester_price':
        if (!value || value.trim() === '') {
          errors.trimester_price = 'Trimester price is required';
        } else if (parseFloat(value) <= 0) {
          errors.trimester_price = 'Trimester price must be greater than 0';
        } else {
          delete errors.trimester_price;
        }
        break;
      
      case 'annual_price':
        if (!value || value.trim() === '') {
          errors.annual_price = 'Annual price is required';
        } else if (parseFloat(value) <= 0) {
          errors.annual_price = 'Annual price must be greater than 0';
        } else {
          delete errors.annual_price;
        }
        break;
      
      default:
        break;
    }
    
    setValidationErrors(errors);
  };

  const validateForm = () => {
    const errors = {};
    
    // Name validation (required)
    if (!formData.name || !formData.name.trim()) {
      errors.name = 'Room type name is required';
    }
    
    // Size validation (required and must be > 0)
    if (!formData.size_sqm) {
      errors.size_sqm = 'Room size is required';
    } else if (parseFloat(formData.size_sqm) <= 0) {
      errors.size_sqm = 'Room size must be greater than 0';
    }
    
    // Semester price validation (required)
    if (!formData.semester_price || formData.semester_price.trim() === '') {
      errors.semester_price = 'Semester price is required';
    } else if (parseFloat(formData.semester_price) <= 0) {
      errors.semester_price = 'Semester price must be greater than 0';
    }
    
    // Trimester price validation (required)
    if (!formData.trimester_price || formData.trimester_price.trim() === '') {
      errors.trimester_price = 'Trimester price is required';
    } else if (parseFloat(formData.trimester_price) <= 0) {
      errors.trimester_price = 'Trimester price must be greater than 0';
    }
    
    // Annual price validation (required)
    if (!formData.annual_price || formData.annual_price.trim() === '') {
      errors.annual_price = 'Annual price is required';
    } else if (parseFloat(formData.annual_price) <= 0) {
      errors.annual_price = 'Annual price must be greater than 0';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAmenityToggle = (amenityId) => {
    setFormData(prev => ({
      ...prev,
      amenity_ids: prev.amenity_ids.includes(amenityId)
        ? prev.amenity_ids.filter(id => id !== amenityId)
        : [...prev.amenity_ids, amenityId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setError('Please correct the errors below before submitting.');
      return;
    }
    
    try {
      setSubmitting(true);
      setError('');
      
      const payload = {
        ...formData,
        property: parseInt(propertyId),
        size_sqm: parseFloat(formData.size_sqm),
        semester_price: formData.semester_price ? parseFloat(formData.semester_price) : null,
        trimester_price: formData.trimester_price ? parseFloat(formData.trimester_price) : null,
        annual_price: formData.annual_price ? parseFloat(formData.annual_price) : null
      };

      await instance.post('/api/room-types/', payload);
      
      setSuccess('Room type created successfully!');
      
      // Refresh the room types list
      fetchRoomTypes();
      
      // Reset form
      setFormData({
        name: '',
        size_sqm: '',
        semester_price: '',
        trimester_price: '',
        annual_price: '',
        description: '',
        amenity_ids: []
      });
      setValidationErrors({});
      
      setTimeout(() => {
        setSuccess('');
      }, 3000);
      
    } catch (error) {
      console.error('Error creating room type:', error);
      
      // Handle specific validation errors
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Check for unique constraint error
        if (errorData.non_field_errors && errorData.non_field_errors[0].includes('unique')) {
          setError('This room type already exists for this property. Please use a different name.');
        } else if (errorData.non_field_errors) {
          setError(errorData.non_field_errors[0]);
        } else if (errorData.name) {
          setError(`Name: ${errorData.name[0]}`);
        } else if (errorData.detail) {
          setError(errorData.detail);
        } else {
          setError(JSON.stringify(errorData));
        }
      } else {
        setError('Failed to create room type. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/provider-properties');
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner color="primary" />
        <p className="mt-2">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-0">Create Room Type</h2>
          <small className="text-muted">Define a new room type for Property #{propertyId}</small>
        </div>
      </div>

      {success && (
        <Alert color="success" className="mb-4">
          <i className="bi bi-check-circle me-2"></i>
          {success}
        </Alert>
      )}

      {error && (
        <Alert color="danger" className="mb-4">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle tag="h5" className="mb-0">
            <i className="bi bi-house-door me-2"></i>
            Room Type Details
          </CardTitle>
        </CardHeader>
        <CardBody>
          <Form onSubmit={handleSubmit} noValidate className="needs-validation">
            {/* Room Type Name */}
            <div className="form-floating mb-3">
              <input
                type="text"
                className={`form-control ${validationErrors.name ? 'is-invalid' : formData.name && !validationErrors.name ? 'is-valid' : ''}`}
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Room Type Name"
                required
              />
              <label htmlFor="name">
                Room Type Name <span className="text-danger">*</span>
              </label>
              {validationErrors.name && (
                <div className="invalid-feedback">
                  {validationErrors.name}
                </div>
              )}
              <small className="form-text text-muted">
                A descriptive name for this room type (e.g., Standard Single, Deluxe Double)
              </small>
            </div>

            <Row>
              {/* Room Size */}
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    type="number"
                    className={`form-control ${validationErrors.size_sqm ? 'is-invalid' : formData.size_sqm && !validationErrors.size_sqm ? 'is-valid' : ''}`}
                    id="size_sqm"
                    name="size_sqm"
                    value={formData.size_sqm}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Room Size (sqm)"
                    step="0.01"
                    min="0.01"
                    required
                  />
                  <label htmlFor="size_sqm">
                    Room Size (sqm) <span className="text-danger">*</span>
                  </label>
                  {validationErrors.size_sqm && (
                    <div className="invalid-feedback">
                      {validationErrors.size_sqm}
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            <Row>
              {/* Semester Price */}
              <Col md={4}>
                <div className="form-floating mb-3">
                  <input
                    type="number"
                    className={`form-control ${validationErrors.semester_price ? 'is-invalid' : formData.semester_price && !validationErrors.semester_price ? 'is-valid' : ''}`}
                    id="semester_price"
                    name="semester_price"
                    value={formData.semester_price}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Semester Price (ZAR)"
                    step="0.01"
                    min="0.01"
                    required
                  />
                  <label htmlFor="semester_price">
                    Semester Price (ZAR) <span className="text-danger">*</span>
                  </label>
                  {validationErrors.semester_price && (
                    <div className="invalid-feedback">
                      {validationErrors.semester_price}
                    </div>
                  )}
                  <small className="form-text text-muted">6 months</small>
                </div>
              </Col>

              {/* Trimester Price */}
              <Col md={4}>
                <div className="form-floating mb-3">
                  <input
                    type="number"
                    className={`form-control ${validationErrors.trimester_price ? 'is-invalid' : formData.trimester_price && !validationErrors.trimester_price ? 'is-valid' : ''}`}
                    id="trimester_price"
                    name="trimester_price"
                    value={formData.trimester_price}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Trimester Price (ZAR)"
                    step="0.01"
                    min="0.01"
                    required
                  />
                  <label htmlFor="trimester_price">
                    Trimester Price (ZAR) <span className="text-danger">*</span>
                  </label>
                  {validationErrors.trimester_price && (
                    <div className="invalid-feedback">
                      {validationErrors.trimester_price}
                    </div>
                  )}
                  <small className="form-text text-muted">4 months</small>
                </div>
              </Col>

              {/* Annual Price */}
              <Col md={4}>
                <div className="form-floating mb-3">
                  <input
                    type="number"
                    className={`form-control ${validationErrors.annual_price ? 'is-invalid' : formData.annual_price && !validationErrors.annual_price ? 'is-valid' : ''}`}
                    id="annual_price"
                    name="annual_price"
                    value={formData.annual_price}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Annual Price (ZAR)"
                    step="0.01"
                    min="0.01"
                    required
                  />
                  <label htmlFor="annual_price">
                    Annual Price (ZAR) <span className="text-danger">*</span>
                  </label>
                  {validationErrors.annual_price && (
                    <div className="invalid-feedback">
                      {validationErrors.annual_price}
                    </div>
                  )}
                  <small className="form-text text-muted">12 months</small>
                </div>
              </Col>
            </Row>

            {/* Description */}
            <FormGroup>
              <Label for="description">Description</Label>
              <Input
                type="textarea"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Optional description of this room type..."
                rows={3}
              />
            </FormGroup>

            {/* Amenities Checkboxes */}
            <FormGroup>
              <Label className="d-block mb-3">
                <strong>Amenities</strong>
                <small className="text-muted d-block">Select all amenities available in this room type</small>
              </Label>
              
              {amenities.length === 0 ? (
                <Alert color="info">
                  <i className="bi bi-info-circle me-2"></i>
                  No amenities available. Create global or custom amenities first.
                </Alert>
              ) : (
                <Row>
                  {amenities.map(amenity => (
                    <Col md={6} lg={4} key={amenity.id}>
                      <FormGroup check className="mb-2">
                        <Input
                          type="checkbox"
                          id={`amenity_${amenity.id}`}
                          checked={formData.amenity_ids.includes(amenity.id)}
                          onChange={() => handleAmenityToggle(amenity.id)}
                        />
                        <Label check for={`amenity_${amenity.id}`}>
                          {amenity.name}
                          {amenity.type === 'local' && (
                            <span className="badge bg-secondary ms-2" style={{ fontSize: '0.7rem' }}>
                              Custom
                            </span>
                          )}
                        </Label>
                      </FormGroup>
                    </Col>
                  ))}
                </Row>
              )}
            </FormGroup>

            {/* Action Buttons */}
            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button
                color="secondary"
                onClick={handleCancel}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                type="submit"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-2"></i>
                    Create Room Type
                  </>
                )}
              </Button>
            </div>
          </Form>
        </CardBody>
      </Card>

      {/* Existing Room Types */}
      {roomTypes.length > 0 && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle tag="h5" className="mb-0">
              <i className="bi bi-list-ul me-2"></i>
              Existing Room Types for this Property ({roomTypes.length})
            </CardTitle>
          </CardHeader>
          <CardBody>
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Size (sqm)</th>
                    <th>Semester (ZAR)</th>
                    <th>Trimester (ZAR)</th>
                    <th>Annual (ZAR)</th>
                    <th>Amenities</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {roomTypes.map((roomType) => (
                    <tr key={roomType.id}>
                      <td>
                        <strong>{roomType.name}</strong>
                        {roomType.description && (
                          <div className="text-muted small">{roomType.description}</div>
                        )}
                      </td>
                      <td>{roomType.size_sqm}</td>
                      <td>{roomType.semester_price ? `R ${parseFloat(roomType.semester_price).toFixed(2)}` : '-'}</td>
                      <td>{roomType.trimester_price ? `R ${parseFloat(roomType.trimester_price).toFixed(2)}` : '-'}</td>
                      <td>{roomType.annual_price ? `R ${parseFloat(roomType.annual_price).toFixed(2)}` : '-'}</td>
                      <td>
                        {roomType.amenities && roomType.amenities.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {roomType.amenities.map((amenity) => (
                              <span 
                                key={amenity.id} 
                                className="badge bg-info"
                                title={amenity.description || amenity.name}
                              >
                                {amenity.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted">No amenities</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${roomType.is_active ? 'bg-success' : 'bg-warning'}`}>
                          {roomType.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default CreateRoomType;
